// src/services/api.js
import axios from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // 30 seconds
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('haazri_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Build full URL for logging
        const fullUrl = config.baseURL + config.url;

        // Log request for debugging with full details
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        console.log(`Full URL: ${fullUrl}`);
        console.log('Request Details:', {
            baseURL: config.baseURL,
            url: config.url,
            method: config.method,
            data: config.data,
            params: config.params,
            headers: config.headers
        });

        return config;
    },
    (error) => {
        console.error('Request Error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
    (response) => {
        // Log successful response
        console.log(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            status: response.status,
            data: response.data
        });

        return response;
    },
    (error) => {
        console.error('API Error:', error);

        // Handle different error types
        if (error.response) {
            // Server responded with error status
            const { status, data } = error.response;
            const requestUrl = error.config?.url || '';

            switch (status) {
                case 401:
                    // Don't redirect if this is a login attempt or password reset
                    if (requestUrl.includes('/login') ||
                        requestUrl.includes('/register') ||
                        requestUrl.includes('/forgot-password') ||
                        requestUrl.includes('/reset-password') ||
                        requestUrl.includes('/verify-email')) {
                        // Let the component handle login errors
                        break;
                    }
                    // Unauthorized - token expired or invalid
                    localStorage.removeItem('haazri_token');
                    localStorage.removeItem('haazri_user');
                    window.location.href = '/login';
                    break;
                case 403: {
                    // Forbidden - check for forced logout conditions (org suspended,
                    // premises deactivated, or license/subscription expired)
                    const errorCode = data?.data?.error_code;
                    if (['ORG_SUSPENDED', 'PREMISES_DEACTIVATED', 'LICENSE_EXPIRED'].includes(errorCode)) {
                        sessionStorage.setItem('haazri_logout_message', data.message || 'Your session has ended. Please contact support.');
                        localStorage.removeItem('haazri_token');
                        localStorage.removeItem('haazri_user');
                        window.location.href = '/login';
                        break;
                    }
                    // client_admin restricted to renewal endpoints while license is expired
                    if (errorCode === 'LICENSE_EXPIRED_RENEWAL_ONLY') {
                        sessionStorage.setItem('haazri_renewal_message', data.message || "Your organization's subscription has expired. Please renew to continue.");
                        if (!window.location.pathname.startsWith('/subscriptions')) {
                            window.location.href = '/subscriptions';
                        }
                        break;
                    }
                    // Forbidden - insufficient permissions
                    console.error('Access denied:', data.message || 'Insufficient permissions');
                    break;
                }
                case 404:
                    // Not found - use the actual error message from API
                    console.error('Not found:', data.message || 'Resource not found');
                    break;
                case 422:
                    // Validation error
                    console.error('Validation error:', data.message || data.errors);
                    break;
                case 500:
                    // Server error
                    console.error('Server error:', data.message || 'Internal server error');
                    break;
                default:
                    console.error(`HTTP ${status}:`, data.message || 'Unknown error');
            }

            // Return formatted error
            const errorMessage = data?.message || `HTTP ${status} Error`;
            return Promise.reject(new Error(errorMessage));
        } else if (error.request) {
            // Network error
            console.error('Network Error:', error.request);
            return Promise.reject(new Error('Network error - please check your connection'));
        } else {
            // Other error
            console.error('Error:', error.message);
            return Promise.reject(error);
        }
    }
);

// API helper functions
export const apiRequest = {
    get: (url, config = {}) => api.get(url, config),
    post: (url, data = {}, config = {}) => api.post(url, data, config),
    put: (url, data = {}, config = {}) => api.put(url, data, config),
    patch: (url, data = {}, config = {}) => api.patch(url, data, config),
    delete: (url, config = {}) => api.delete(url, config),
};

// Upload helper for file uploads
export const uploadFile = (url, formData, onProgress = null) => {
    return api.post(url, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
            if (onProgress) {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
                onProgress(percentCompleted);
            }
        },
    });
};

// Download helper for file downloads
export const downloadFile = async (url, filename, onProgress = null) => {
    try {
        const response = await api.get(url, {
            responseType: 'blob',
            onDownloadProgress: (progressEvent) => {
                if (onProgress && progressEvent.total) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percentCompleted);
                }
            }
        });

        // Create download link
        const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        // Clean up
        window.URL.revokeObjectURL(downloadUrl);

        return response;
    } catch (error) {
        console.error('Download Error:', error);
        throw error;
    }
};

// Export configured axios instance
export default api;
